import React, { useState, useEffect, useRef } from "react";
import { FaTimes } from "react-icons/fa";

/**
 * Reusable Verification Code Modal Component
 * Displays a modal for entering a 6-digit verification code
 * Features:
 * - Auto-focus on first input
 * - Auto-advance to next input
 * - Paste support for all 6 digits
 * - Countdown timer
 * - Resend functionality
 */
const VerificationCodeModal = ({
  isOpen,
  onClose,
  onVerify,
  onResend,
  email,
  timeLimit = 60, // Time limit in seconds
  title = "Enter Verification Code",
  description = "We've sent a 6-digit code to",
}) => {
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [timeLeft, setTimeLeft] = useState(timeLimit);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const inputRefs = [
    useRef(),
    useRef(),
    useRef(),
    useRef(),
    useRef(),
    useRef(),
  ];

  // Reset timer when modal opens
  useEffect(() => {
    if (isOpen) {
      setTimeLeft(timeLimit);
      setCode(["", "", "", "", "", ""]);
      setError("");
      // Focus first input after modal opens
      setTimeout(() => inputRefs[0].current?.focus(), 100);
    }
  }, [isOpen, timeLimit]);

  // Countdown timer
  useEffect(() => {
    if (!isOpen || timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isOpen, timeLeft]);

  const handleChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError("");

    // Auto-advance to next input
    if (value && index < 5) {
      inputRefs[index + 1].current?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs[index - 1].current?.focus();
    }
    // Handle left arrow
    else if (e.key === "ArrowLeft" && index > 0) {
      inputRefs[index - 1].current?.focus();
    }
    // Handle right arrow
    else if (e.key === "ArrowRight" && index < 5) {
      inputRefs[index + 1].current?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").trim();

    // Check if pasted data is 6 digits
    if (/^\d{6}$/.test(pastedData)) {
      const newCode = pastedData.split("");
      setCode(newCode);
      setError("");
      // Focus last input
      inputRefs[5].current?.focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const fullCode = code.join("");

    if (fullCode.length !== 6) {
      setError("Please enter all 6 digits");
      return;
    }

    if (timeLeft <= 0) {
      setError("Code has expired. Please request a new one.");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      await onVerify(fullCode);
    } catch (err) {
      setError(err.message || "Invalid code. Please try again.");
      setCode(["", "", "", "", "", ""]);
      inputRefs[0].current?.focus();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResend = async () => {
    setError("");
    setCode(["", "", "", "", "", ""]);
    setTimeLeft(timeLimit);
    inputRefs[0].current?.focus();

    try {
      await onResend();
    } catch (err) {
      setError(err.message || "Failed to resend code");
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 backdrop-blur-sm px-4">
      <div className="bg-white rounded-2xl shadow-[0_10px_40px_rgba(0,0,0,0.2)] w-full max-w-md p-5 sm:p-8 m-0 sm:m-4 relative animate-fadeIn">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          type="button"
        >
          <FaTimes size={20} />
        </button>

        {/* Header */}
        <div className="text-center mb-4 sm:mb-6">
          <h2 className="text-xl sm:text-2xl font-bold text-[#2f3542] mb-2">{title}</h2>
          <p className="text-[#57606f] text-xs sm:text-sm">{description}</p>
          <p className="text-[#3498db] font-semibold mt-1 text-sm sm:text-base break-all">{email}</p>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Code inputs */}
          <div className="flex justify-center gap-1.5 sm:gap-2 mb-4 sm:mb-6">
            {code.map((digit, index) => (
              <input
                key={index}
                ref={inputRefs[index]}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                className={`w-10 h-12 sm:w-12 sm:h-14 text-center text-xl sm:text-2xl font-bold border-2 rounded-lg transition-all ${
                  error
                    ? "border-[#dc3545] bg-[#fff5f5]"
                    : "border-[#ddd] focus:border-[#3498db] focus:shadow-[0_0_0_3px_rgba(52,152,219,0.1)]"
                } focus:outline-none`}
                disabled={isSubmitting || timeLeft <= 0}
              />
            ))}
          </div>

          {/* Timer */}
          <div className="text-center mb-4">
            <p
              className={`text-sm font-semibold ${
                timeLeft <= 10 ? "text-[#dc3545]" : "text-[#57606f]"
              }`}
            >
              {timeLeft > 0 ? (
                <>Time remaining: {formatTime(timeLeft)}</>
              ) : (
                <>Code expired</>
              )}
            </p>
          </div>

          {/* Error message */}
          {error && (
            <div className="mb-4 p-3 bg-[#fff5f5] border border-[#dc3545] rounded-lg">
              <p className="text-[#dc3545] text-sm text-center flex items-center justify-center gap-2">
                <span>⚠</span>
                {error}
              </p>
            </div>
          )}

          {/* Submit button */}
          <button
            type="submit"
            disabled={
              isSubmitting || code.join("").length !== 6 || timeLeft <= 0
            }
            className="w-full h-11 bg-[#3498db] text-white border-none rounded-lg cursor-pointer transition-all duration-300 text-base font-semibold uppercase tracking-[0.5px] shadow-[0_4px_15px_rgba(52,152,219,0.3)] hover:scale-105 hover:shadow-[0_6px_20px_rgba(52,152,219,0.4)] active:scale-100 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
          >
            {isSubmitting ? "Verifying..." : "Verify Code"}
          </button>

          {/* Resend button */}
          <div className="text-center mt-4">
            <button
              type="button"
              onClick={handleResend}
              disabled={timeLeft > 0 || isSubmitting}
              className="text-sm text-[#3498db] bg-transparent border-none cursor-pointer hover:underline disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline"
            >
              Didn't receive the code? Resend
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VerificationCodeModal;
